/**
 * Le **bac à sable local** : le moteur pur tourne dans le navigateur.
 *
 * Assumé et étiqueté dans l'UI (bandeau « bac à sable »). Aucune auth, aucun appel réseau,
 * aucune autorité : l'état vit en mémoire et se recopie dans `localStorage` à chaque action,
 * pour survivre à un F5. C'est le prix à payer pour que le jeu soit **jouable tout de suite**
 * pendant que la session serveur monte la simulation autoritative.
 *
 * Ce que ce fichier ne fait PAS, et ne fera jamais : calculer une règle de jeu. Il appelle
 * `resolve()` et range le résultat. Toute la simulation est dans `@maxime/shared`.
 *
 * ⚠️ **Limite connue et voulue** : `localStorage` est réinscriptible par le joueur, donc le
 * bac à sable est trivialement trichable. C'est exactement pourquoi PLAN §6 met la simulation
 * autoritative côté serveur. Rien de ce module ne survivra à la bascule sauf son interface.
 */

import {
  bootstrapRun,
  initialState,
  resolve,
  type Action,
  type EngineEvent,
  type GameState,
  type LogEntry,
  type Recapitulatif,
  type RecapDelegue,
} from "@maxime/shared";

import { contenu } from "./content";
import { modeTricheActif } from "./dev";
import { nouveauxEtages, etage, type EtageId } from "./escalier";
import type {
  CreationRun,
  DispatchResult,
  GameClient,
  JournalEntry,
  Progression,
  RunSnapshot,
} from "./gameClient";
import { archiverRunLocale, raisonDeFinLocale } from "./historiqueLocal";
import { entreeInterface, entreesDeResolution, ligneDeverrouillage, limiterJournal } from "./journal";

/** Clé de persistance. Versionnée : un changement de forme jette proprement l'ancienne run. */
const CLE = "maxime.bac-a-sable.run.v1";

/**
 * ⟦C17⟧ **Y a-t-il une partie bac à sable à charger ?** — un simple coup d'œil à la clé, sans
 * instancier `LocalGameClient` ni parser le JSON : l'écran-titre (`sessionStore`, `Menu.tsx`)
 * s'en sert pour décider si « Charger » est grisé, AVANT de savoir dans quel mode le joueur va
 * choisir de jouer. Un faux positif est possible en théorie (une clé au mauvais schéma, rejetée
 * ensuite par `charger()`) — accepté : le risque est un bouton actif qui ramène gentiment au
 * menu plutôt qu'un bouton grisé qui cache une vraie partie.
 */
export function existeSauvegardeLocale(): boolean {
  try {
    return localStorage.getItem(CLE) !== null;
  } catch {
    return false;
  }
}

/** Graine aléatoire de création (brief). `crypto` du navigateur — le moteur, lui, reste pur. */
function graineAleatoire(): string {
  const octets = new Uint8Array(8);
  crypto.getRandomValues(octets);
  return Array.from(octets, (o) => o.toString(16).padStart(2, "0")).join("");
}

/** Compteur d'ids de journal — reprend au max existant pour rester croissant après un F5. */
function prochainId(journal: readonly JournalEntry[]): number {
  return journal.reduce((max, e) => Math.max(max, e.id), 0) + 1;
}

/**
 * Le bloc `progression` que le serveur enverra (SPEC-API §5.5 + M2.7), reconstitué depuis les
 * `EngineEvent` du tour.
 *
 * ⚠️ **Un écart connu, et qui compte** : `retour_au_jalon` reste **toujours `null`** ici. Le
 * retour au jalon en mode normal — argent ÷2, réputation à 50, nœuds conservés — est une
 * mécanique de **persistance serveur** (§7.3), pas du moteur : `verifierGameOver` se contente
 * de poser `status = "game_over"`. En bac à sable, un game over en mode normal **termine donc
 * la run** là où le serveur la ferait repartir du palier précédent. C'est la seule divergence
 * de règle connue entre les deux implémentations, et elle disparaît avec `serverGameClient`.
 */
function progressionDe(events: readonly EngineEvent[], state: GameState): Progression {
  const paliers = events
    .filter((e) => e.kind === "milestone" && typeof e.data.palier === "number")
    .map((e) => e.data.palier as number);

  const victoire = events.find((e) => e.kind === "victory");
  const gameOver = events.find((e) => e.kind === "game_over");

  return {
    paliers_franchis: paliers,
    retour_au_jalon: null,
    // En hardcore la run est close ; en normal, le bac à sable la ferme aussi, faute de la
    // restauration serveur — on le dit ici plutôt que de faire croire à une fin définitive.
    fin_verrouillee:
      gameOver && state.mode === "hardcore" && typeof gameOver.data.cause === "string"
        ? gameOver.data.cause
        : null,
    victoire: victoire
      ? {
          ending: state.ending,
          score: null,
          turn: state.turn,
        }
      : null,
  };
}

export class LocalGameClient implements GameClient {
  readonly kind = "local" as const;

  /** Le bac à sable sait tout faire : c'est le moteur entier, dans l'onglet. */
  readonly capacites = { production: true } as const;

  #run: RunSnapshot | null = null;

  getState(): RunSnapshot | null {
    return this.#run;
  }

  async charger(): Promise<RunSnapshot | null> {
    if (this.#run) return this.#run;
    const brut = localStorage.getItem(CLE);
    if (!brut) return null;
    try {
      const run = JSON.parse(brut) as RunSnapshot;
      // Garde minimale : un état d'une autre version de schéma ne se rejoue pas.
      // ⟦SPEC-FIL §9.6 / M3.21⟧ **Les deux versions sont acceptées** : le Fil a fait passer le
      // schéma de 1 à 2, et la migration est vide — tous ses champs sont optionnels et leur
      // absence vaut leur valeur neutre (§10). Une partie commencée avant le Fil se rejoue
      // donc telle quelle, et démarre son Fil quand ses abonnés franchissent le seuil.
      if (run.state?.schema_version !== 1 && run.state?.schema_version !== 2) {
        localStorage.removeItem(CLE);
        return null;
      }
      this.#run = run;
      return run;
    } catch {
      localStorage.removeItem(CLE);
      return null;
    }
  }

  async creerRun(input: CreationRun): Promise<RunSnapshot> {
    const c = contenu();
    const seed = input.seed?.trim() || graineAleatoire();
    // §5 : `initialState` rend un état sans monde ; `bootstrapRun` pose les 3 tendances et
    // les 3 cartes de départ, de façon déterministe pour ce seed.
    // ⟦A-4 §2⟧ Le genre entre dans le `GameState` : depuis la validation du 14/08 il est
    // MÉCANIQUE (traits permanents, `engine/traits.ts`), plus seulement une métadonnée de run.
    // Sans ce paramètre, la fiche de run affichée à la création annoncerait des ×1,2 qui ne
    // s'appliqueraient nulle part.
    const state = bootstrapRun(initialState(input.mode, input.genre), c, seed);

    const run: RunSnapshot = {
      runId: `bac-a-sable-${seed}`,
      channelName: input.channelName.trim(),
      genre: input.genre,
      mode: input.mode,
      seed,
      state,
      journal: [
        entreeInterface(state, 1, {
          icone: "🎬",
          ton: "info",
          texte: `« ${input.channelName.trim()} » est née. Zéro abonné, une caméra, et une rue à décrocher.`,
        }),
      ],
      // Au départ, l'escalier A-5 est fermé : le jeu tient en deux boutons. Aucun étage
      // n'est marqué « vu », donc chacun aura son moment quand il s'ouvrira.
      unlocksSeen: [],
      contentVersion: c.meta.content_version,
      createdAt: new Date().toISOString(),
      bacASable: true,
    };
    this.#ecrire(run);
    return run;
  }

  /**
   * `contexte` (les timelines d'Atelier) est **ignoré**, et c'est le point : en bac à sable, le
   * score du mini-jeu fait foi puisqu'il n'y a personne à convaincre. Les timelines sont quand
   * même produites par les cinq jeux — c'est ce qui garantit qu'elles ne pourrissent pas dans un
   * chemin de code que personne n'exécute jamais.
   */
  async dispatch(action: Action): Promise<DispatchResult> {
    const run = this.#run;
    if (!run) throw new Error("Aucune run en cours.");
    const c = contenu();

    // ── LA ligne de code qui fait le jeu. Tout le reste est de la mise en scène. ──
    const {
      state,
      events,
      log,
      // ⟦A-16⟧ La décomposition des jours écoulés vient du moteur, entière. On la fait
      // suivre telle quelle : rien ici ne la relit, ne l'agrège ni ne la recompose.
      recap,
      // ⟦SPEC-PRODUCTEUR §5.5 / A-66⟧ Idem pour la délégation. Déstructuré ICI en même temps
      // que le serveur le fait de son côté : c'est la parité ⟦A-32⟧, et elle se perd toujours
      // en ajoutant un champ d'un seul côté.
      delegation,
    }: {
      state: GameState;
      events: EngineEvent[];
      log: LogEntry[];
      recap?: Recapitulatif;
      delegation?: RecapDelegue;
    } = resolve(run.state, action, run.seed, c);

    let id = prochainId(run.journal);
    // ⟦C57 · lot 3⟧ Le récap des jours rejoint le Journal (détail rouvrable) : `recap` et l'action qui l'a produit.
    const nouvellesEntrees = entreesDeResolution(events, log, state, id, recap ? { recap, action: action.type } : null);
    id += nouvellesEntrees.length;

    // ⟦A-5⟧ Chaque ouverture est un moment de jeu, jamais un silence.
    const deverrouillages = nouveauxEtages(state, run.unlocksSeen);
    for (const cle of deverrouillages) {
      const e = etage(cle);
      nouvellesEntrees.push(
        entreeInterface(state, id++, ligneDeverrouillage(e)),
      );
    }

    const suivante: RunSnapshot = {
      ...run,
      state,
      journal: limiterJournal([...run.journal, ...nouvellesEntrees]),
      unlocksSeen: [...run.unlocksSeen, ...deverrouillages],
    };
    this.#ecrire(suivante);

    const publiee = events.find((e) => e.kind === "video_published");
    return {
      run: suivante,
      events,
      log,
      nouvellesEntrees,
      deverrouillages,
      videoPubliee: publiee && typeof publiee.data.video_id === "number" ? publiee.data.video_id : null,
      progression: progressionDe(events, state),
      recap: recap ?? null,
      delegation: delegation ?? null,
    };
  }

  /**
   * ⟦A-97 §3⟧ Abandonner en bac à sable — **la fiche s'écrit AVANT l'effacement.**
   *
   * L'ordre est tout : après le `removeItem`, il n'y a plus rien à archiver — ni abonnés, ni
   * jours simulés, ni vidéos. C'est la seule différence avec le code d'avant, et c'est ce qui
   * permet de tenir les deux moitiés de la phrase dite au joueur : la partie est bien effacée
   * de ce navigateur (elle l'est, `CLE` disparaît), et sa fiche reste dans l'historique (elle
   * vit sous une AUTRE clé, que ceci ne touche pas).
   *
   * `"abandoned"` en dur : c'est la seule des huit `ended_reason` que le moteur ne connaît pas,
   * parce que ce n'est pas une règle de jeu — l'état, lui, reste `active` jusqu'au bout.
   */
  async abandonner(): Promise<void> {
    if (this.#run) archiverRunLocale(this.#run, "abandoned");
    this.#run = null;
    localStorage.removeItem(CLE);
  }

  /**
   * Remplace la run **en place**, sans passer par `resolve` — l'unique chemin propre pour
   * écrire un état fabriqué depuis l'interface (la télécommande de `game/dev.ts`).
   *
   * Ce n'est pas une brèche : le bac à sable est trivialement modifiable par construction, son
   * état vit dans `localStorage` et l'en-tête de ce fichier le dit déjà. Ce qui compte, c'est
   * que la méthode passe par `#ecrire`, donc que le cache mémoire et le stockage restent
   * d'accord — un état écrit à côté du cache repartirait à la première action.
   *
   * Elle n'existe **pas** sur le contrat `GameClient` : le jour du `ServerGameClient`, aucun
   * appelant ne pourra la confondre avec une opération de jeu. Et son corps est gardé au
   * **runtime** ⟦A-176⟧ : hors développement, elle refuse tant que le mode triche n'est pas armé
   * (`modeTricheActif`, `game/dev.ts`) ; le panneau caché ne suffit pas, c'est ce refus qui tient
   * la promesse « rien sans opt-in » même si un appelant l'atteignait par un autre chemin.
   */
  async remplacerRun(run: RunSnapshot): Promise<RunSnapshot> {
    if (!import.meta.env.DEV && !modeTricheActif()) {
      throw new Error("Réservé au mode triche du bac à sable.");
    }
    if (!this.#run) throw new Error("Aucune run en cours.");
    this.#ecrire(run);
    return run;
  }

  #ecrire(run: RunSnapshot): void {
    this.#run = run;
    try {
      localStorage.setItem(CLE, JSON.stringify(run));
    } catch {
      // Quota plein ou mode privé : la partie continue en mémoire, elle ne survivra pas au F5.
      // On ne casse pas la session du joueur pour ça.
    }
    // ⟦A-97 §3⟧ **Le game over s'archive ici, pas dans `resolve`** — parce que `#ecrire` est le
    // chemin d'écriture UNIQUE de ce client (`resolve`, `creerRun`, `remplacerRun` y passent
    // tous). Poser le crochet au seul endroit par où tout passe, c'est la garantie qu'aucune
    // fin ne s'échappe : une chaîne morte pendant une semaine déléguée est archivée comme une
    // chaîne morte à la publication.
    //
    // `archiverRunLocale` est idempotent par `runId` : réécrire l'état d'une run déjà finie
    // (un F5, l'écran de fin qui se recharge) remplace sa fiche, il n'en empile pas une
    // deuxième. Et un état non terminal ne rend aucune raison — donc n'archive rien.
    const raison = raisonDeFinLocale(run.state);
    if (raison) archiverRunLocale(run, raison);
  }
}

/** L'instance unique consommée par les stores. Le jour du serveur : `new ServerGameClient()`. */
const instance = new LocalGameClient();

export const gameClient: GameClient = instance;

/**
 * La **même** instance, typée large. Ne sert qu'à ce qui est explicitement propre au bac à
 * sable (aujourd'hui : `remplacerRun`, pour la télécommande de dev). Tout le reste du client
 * passe par `gameClient`, qui ne montre que le contrat commun avec le serveur.
 */
export const bacASable = instance;

export type { EtageId };
